import React, { useState, useEffect } from 'react'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'

import UserProfile from '../user'
import { getLabelNames, addLabelName, addLabels } from '../api'

const LANGUAGES = ["Python", "JavaScript", "C++", "Java", "C", "Go"]
const COMMIT_TYPES = ["Peer Review", "Documentation", "Bug/Issue Fix", "Feature", "Process Change"]
const SCALE = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

const LabelsDiv = () => {
    const [existingLabels, setExistingLables] = useState([])
    const [labeled, setLabeled] = useState([])
    const [commitId, setCommitId] = useState("")
    const [commitLink, setCommitLink] = useState("")
    const [language, setLanguage] = useState("")
    const [fetching, setFetching] = useState(false)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        getLabelNames().then(setExistingLables).catch(error => console.log(error))
    }, [])

    const createNewLabel = async (e) => {
        e.preventDefault()
        const form = e.target
        const labelName = form[0].value.trim()
        if (!labelName) return
        const taken = [...existingLabels, ...labeled].some(l => l.toLowerCase() === labelName.toLowerCase())
        if (taken) {
            window.alert("That label already exists.")
            return
        }
        try {
            await addLabelName(labelName)
            setExistingLables([...existingLabels, labelName])
            form.reset()
        } catch (error) {
            window.alert(`Could not create the label: ${error.message}`)
        }
    }

    const addToLabeled = (label) => {
        setLabeled([...labeled, label])
        setExistingLables(existingLabels.filter(l => l !== label))
    }

    const removeFromLabeled = (label) => {
        setExistingLables([...existingLabels, label])
        setLabeled(labeled.filter(l => l !== label))
    }

    const getNewCommit = async (e) => {
        e.preventDefault()
        if (!language) {
            window.alert("Choose a programming language first.")
            return
        }
        setFetching(true)
        try {
            const randomPageNum = Math.floor(Math.random() * 25) + 1
            const search = await fetch(
                `https://api.github.com/search/repositories?q=language:${encodeURIComponent(language)}&per_page=30&page=${randomPageNum}`
            ).then(res => res.json())
            if (!search.items || search.items.length === 0) throw new Error("GitHub is busy right now. Wait a minute and try again.")
            const repo = search.items[Math.floor(Math.random() * search.items.length)]

            const commits = await fetch(`${repo.url}/commits?per_page=5&page=${randomPageNum}`).then(res => res.json())
            if (!Array.isArray(commits) || commits.length === 0) throw new Error("That repository had no commits to show. Please try again.")
            const commit = commits[Math.floor(Math.random() * commits.length)]
            setCommitId(commit.sha)
            setCommitLink(commit.html_url)
        } catch (error) {
            window.alert(`Could not get a commit: ${error.message}`)
        } finally {
            setFetching(false)
        }
    }

    const postLabels = async (e) => {
        e.preventDefault()
        const commitType = e.target[0].value
        const github = UserProfile.getName()
        if (commitId === '') {
            window.alert("Please get a commit to label first.")
            return
        } else if (labeled.length === 0 || commitType === 'type of commit') {
            window.alert("Please choose the type of commit and at least one label to score.")
            return
        } else if (github === "") {
            window.alert("Please sign up on the home page first.")
            return
        }
        // Form fields: [type of commit, then a score and a confidence select per label, ..., submit]
        const data = []
        for (let i = 0; i < labeled.length; i++) {
            const label = labeled[i]
            const score = e.target[1 + 2 * i].value
            const confidence = e.target[2 + 2 * i].value
            if (score === `score ${label}`) {
                window.alert(`Please give a score for “${label}”.`)
                return
            }
            if (confidence === `confidence ${label}`) {
                window.alert(`Please give your confidence for “${label}”.`)
                return
            }
            data.push({
                "commit link": commitLink,
                "commit id": commitId,
                "type of commit": commitType,
                "label": label,
                "github profile": github,
                "score": score,
                "confidence": confidence,
            })
        }
        setSaving(true)
        try {
            await addLabels(data)
            window.alert("Your labels are saved. Thank you for your contribution!")
            e.target.reset()
        } catch (error) {
            window.alert(`Could not save your labels: ${error.message}`)
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="ocn-labels-grid">
            <aside className="ocn-card">
                <h2><span className="ocn-step">1</span>Get a commit</h2>
                <p className="ocn-hint">Pick a language to get a random commit from a popular repository.</p>
                <Form onSubmit={getNewCommit}>
                    <Form.Select
                        id="commit_language"
                        className="mb-2"
                        aria-label="Programming language"
                        value={language}
                        onChange={e => setLanguage(e.target.value)}
                    >
                        <option value="">Select language</option>
                        {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                    </Form.Select>
                    <Button variant="primary" type="submit" className="w-100" disabled={fetching}>
                        {fetching ? 'Finding a commit…' : (commitId ? 'Get another commit' : 'Get a commit')}
                    </Button>
                </Form>

                <div className="ocn-commit">
                    {commitId ? (
                        <>
                            <p className="ocn-eyebrow" style={{ margin: 0 }}>Commit</p>
                            <div className="sha">{commitId.slice(0, 12)}</div>
                            <div className="repo">{commitLink.replace('https://github.com/', '').split('/commit/')[0]}</div>
                            <Button variant="outline-secondary" href={commitLink} target="_blank" rel="noopener noreferrer" className="w-100">
                                Open on GitHub ↗
                            </Button>
                        </>
                    ) : (
                        <span className="ocn-empty">No commit yet. Your commit will appear here.</span>
                    )}
                </div>
            </aside>

            <div className="ocn-labels-main">
                <section className="ocn-card">
                    <h2><span className="ocn-step">2</span>Choose labels</h2>
                    <p className="ocn-hint">Select the attributes you want to rate for this commit. Select a chosen label again to remove it.</p>
                    <p className="ocn-eyebrow">Selected</p>
                    <div className="ocn-chips" style={{ marginBottom: '1rem' }}>
                        {labeled.length === 0 && <span className="ocn-empty">No labels selected yet.</span>}
                        {labeled.map(label => (
                            <button type="button" key={label} className="ocn-chip selected" onClick={() => removeFromLabeled(label)} aria-label={`Remove ${label}`}>
                                {label} <span className="sym" aria-hidden="true">×</span>
                            </button>
                        ))}
                    </div>
                    <p className="ocn-eyebrow">Available</p>
                    <div className="ocn-chips">
                        {existingLabels.map(label => (
                            <button type="button" key={label} className="ocn-chip" onClick={() => addToLabeled(label)} aria-label={`Add ${label}`}>
                                <span className="sym" aria-hidden="true">+</span> {label}
                            </button>
                        ))}
                    </div>
                    <Form onSubmit={createNewLabel} className="ocn-newlabel">
                        <Form.Control id="new_label" aria-label="New label name" placeholder="Add a new label" maxLength={50} />
                        <Button variant="outline-secondary" type="submit">Add</Button>
                    </Form>
                </section>

                <section className="ocn-card">
                    <h2><span className="ocn-step">3</span>Score and submit</h2>
                    <p className="ocn-hint">Rate each label from 1 (low) to 10 (high), and say how confident you are in each rating.</p>
                    <Form onSubmit={postLabels}>
                        <Form.Group className="mb-3" style={{ maxWidth: '22rem' }}>
                            <Form.Label htmlFor="commit_type">Type of commit</Form.Label>
                            <Form.Select id="commit_type" defaultValue="type of commit">
                                <option value="type of commit">Select type</option>
                                {COMMIT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </Form.Select>
                        </Form.Group>

                        {labeled.length > 0 ? (
                            <>
                                <div className="ocn-score-head">
                                    <span className="name">Label</span><span>Score</span><span>Confidence</span>
                                </div>
                                {labeled.map(label => (
                                    <div className="ocn-score-row" key={label}>
                                        <span className="name">{label}</span>
                                        <Form.Select aria-label={`Score for ${label}`} defaultValue={`score ${label}`}>
                                            <option value={`score ${label}`}>Score</option>
                                            {SCALE.map(n => <option key={n} value={n}>{n}</option>)}
                                        </Form.Select>
                                        <Form.Select aria-label={`Confidence for ${label}`} defaultValue={`confidence ${label}`}>
                                            <option value={`confidence ${label}`}>Confidence</option>
                                            {SCALE.map(n => <option key={n} value={n}>{n}</option>)}
                                        </Form.Select>
                                    </div>
                                ))}
                            </>
                        ) : (
                            <p className="ocn-empty">Choose at least one label in step 2 to score it here.</p>
                        )}

                        <Button type="submit" className="mt-3" disabled={saving}>{saving ? 'Saving…' : 'Submit labels'}</Button>
                    </Form>
                </section>
            </div>
        </div>
    )
}

export default LabelsDiv
